/**
 * Persisted "last successfully registered FCM token" slot.
 *
 * Each product remembers the token it registered under its own app-owned
 * storage key so logout can deregister exactly what THIS browser registered.
 * Storage failures never throw: a set that fails simply means
 * deregister-on-logout becomes a no-op, which is a state the backend's
 * device sweep handles.
 */

export interface TokenStore {
  /** The remembered token, or `null` (also when storage is unavailable). */
  get(): string | null;
  /** Remember a token. Swallows storage failures. */
  set(token: string): void;
  /** Forget the remembered token. Swallows storage failures. */
  remove(): void;
}

export function createTokenStore(storageKey: string): TokenStore {
  return {
    get(): string | null {
      try {
        return localStorage.getItem(storageKey);
      } catch {
        return null;
      }
    },
    set(token: string): void {
      try {
        localStorage.setItem(storageKey, token);
      } catch {
        // Storage unavailable — registration still succeeded; logout will
        // simply have no remembered token to deregister.
      }
    },
    remove(): void {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // Storage unavailable — nothing to clear.
      }
    },
  };
}
